import type { ComponentType } from "react";
import type { ZodType } from "zod";
import { LinearIssuesCard, linearIssuesSchema } from "../components/previews/LinearIssuesCard";
import { NotionSearchCard, notionSearchSchema } from "../components/previews/NotionSearchCard";
import { TodoistTasksCard, todoistTasksSchema } from "../components/previews/TodoistTasksCard";
import type { McpToolPreview } from "./sseEvents";

export interface PreviewCardProps<T> {
  data: T;
  connectorName: string;
}

interface CardEntry<T = unknown> {
  schema: ZodType<T>;
  Component: ComponentType<PreviewCardProps<T>>;
}

/** Erase the card's payload generic — pairs stay type-checked at definition. */
function defineCard<T>(
  schema: ZodType<T>,
  Component: ComponentType<PreviewCardProps<T>>,
): CardEntry {
  return { schema, Component } as unknown as CardEntry;
}

/**
 * `(catalog connector slug):(original MCP tool name)` → typed card. Several
 * tool names may map to one card. Custom connectors have a null slug and never
 * match. Tool names were taken from the live servers' tools/list — Todoist
 * (ai.todoist.net/mcp), Notion (mcp.notion.com/mcp), Linear
 * (mcp.linear.app/mcp); if a server renames a tool the card silently stops
 * matching and the text fallback takes over (re-verify against
 * `user_mcp_connections.cached_tools` when that happens).
 */
const registry: Record<string, CardEntry> = {
  "todoist:find-tasks": defineCard(todoistTasksSchema, TodoistTasksCard),
  "todoist:find-tasks-by-date": defineCard(todoistTasksSchema, TodoistTasksCard),
  "notion:notion-search": defineCard(notionSearchSchema, NotionSearchCard),
  "notion:notion-fetch": defineCard(notionSearchSchema, NotionSearchCard),
  "linear:list_issues": defineCard(linearIssuesSchema, LinearIssuesCard),
};

/**
 * Resolve a preview to its typed card, runtime-validating the attacker-
 * influenceable `structured` payload. Returns null (→ the caller falls back to
 * the text preview) when there is no registry match or validation fails —
 * `safeParse` only, a malformed payload must never throw into the thread.
 */
export function resolvePreviewCard(
  preview: McpToolPreview,
): { Component: ComponentType<PreviewCardProps<unknown>>; data: unknown } | null {
  if (!preview.connector_slug || !preview.tool_name || preview.structured == null) return null;
  const entry = registry[`${preview.connector_slug}:${preview.tool_name}`];
  if (!entry) return null;
  const parsed = entry.schema.safeParse(preview.structured); // NEVER .parse — no throws
  return parsed.success ? { Component: entry.Component, data: parsed.data } : null;
}
